import { useState, useEffect } from "react";
import { z } from "zod";
import logo from '@/assets/logo.png';
import defaultFooterHealthImage from '@/assets/footer-health-illustration.jpg';
import { NavLink } from "@/components/NavLink";
import { supabase } from "@/integrations/supabase/client";
import { Facebook, Instagram, Twitter, Linkedin, Mail, ExternalLink, Youtube } from "lucide-react";

// Custom TikTok icon (lucide-react doesn't have one)
const TikTokIcon = ({ size = 18, className }: { size?: number; className?: string }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useStickyCart } from "@/contexts/StickyCartContext";
import { useCMSSiteSettings, useCategories, useProducts } from "@/hooks/use-cms-api";

// Helper functions to transform CMS data structures
const transformSocialLinks = (cmsLinks: Record<string, { enabled: boolean; url: string }> | null) => {
  const defaults = ['facebook', 'instagram', 'twitter', 'linkedin', 'tiktok', 'youtube'];
  if (!cmsLinks) return Object.fromEntries(
    defaults.map(k => [k, { enabled: false, url: '' }])
  );
  
  return Object.fromEntries(
    defaults.map(key => [
      key,
      cmsLinks[key] || { enabled: false, url: '' }
    ])
  );
};

const transformQuickLinks = (cmsLinks: Array<{ id: string; label: string; url: string; enabled: boolean; display_order: number }> | null) => {
  if (!cmsLinks) return [];
  return cmsLinks.filter(link => link.enabled).sort((a, b) => a.display_order - b.display_order);
};

const transformAdditionalServices = (cmsServices: Array<{ id: string; label: string; url: string; enabled: boolean; display_order: number }> | null) => {
  if (!cmsServices) return [];
  return cmsServices.filter(service => service.enabled).sort((a, b) => a.display_order - b.display_order);
};

export function Footer() {
  const { toast } = useToast();
  const { isStickyCartVisible } = useStickyCart();
  
  // Fetch data from CMS API
  const { data: siteSettings, isLoading: settingsLoading } = useCMSSiteSettings();
  const { data: cmsCategories, isLoading: categoriesLoading } = useCategories();
  const { data: productsResponse } = useProducts({ limit: 200 });

  // Local state for admin check and newsletter
  const [isAdmin, setIsAdmin] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Transform CMS data to Footer format
  const footerLogo = siteSettings?.footer_logo_url || logo;
  const footerHealthImage = siteSettings?.footer_health_image_url || defaultFooterHealthImage;
  const companyDescription = siteSettings?.footer_company_description || "Your trusted source for premium health supplements.";
  const socialLinks = transformSocialLinks(siteSettings?.footer_social_links);
  const quickLinks = transformQuickLinks(siteSettings?.footer_quick_links);
  const additionalServices = transformAdditionalServices(siteSettings?.footer_additional_services);
  const topProducts = productsResponse?.products?.slice(0, 7) || [];
  const categories = cmsCategories || [];
  const footerCategoriesConfig = siteSettings?.footer_categories || [];
  const sectionsOrder = siteSettings?.footer_sections_order?.filter(section => section.enabled) || [
    { id: "company", name: "Company Info", enabled: true },
    { id: "quick_links", name: "Quick Links", enabled: true },
    { id: "top_products", name: "Top Selling Products", enabled: true },
    { id: "health_image", name: "Health Illustration", enabled: true },
  ];
  const trustBadges = siteSettings?.trust_badges?.filter(b => b.enabled) || [];
  const paymentMethods = siteSettings?.payment_methods?.filter(m => m.enabled) || [];

  const emailSchema = z.string()
    .trim()
    .email({ message: "Please enter a valid email address" })
    .max(255, { message: "Email must be less than 255 characters" });

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsAdmin(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();

      if (error) {
        console.error('Error checking admin role:', error);
        setIsAdmin(false);
        return;
      }

      setIsAdmin(!!data);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    const validation = emailSchema.safeParse(newsletterEmail);
    if (!validation.success) {
      toast({
        title: "Invalid Email",
        description: validation.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        "subscribe-newsletter",
        {
          body: { email: validation.data },
        }
      );

      if (error) throw error;

      toast({
        title: "Success!",
        description: data.message || "You've been subscribed to our newsletter!",
      });
      setNewsletterEmail("");
    } catch (error: any) {
      console.error("Newsletter subscription error:", error);
      toast({
        title: "Subscription Failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case "company":
        return (
          <div key="company">
            <img src={footerLogo} alt="GetTested" className="h-6 mb-4" />
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              {companyDescription}
            </p>
            
            <div className="mb-6">
              <p className="font-semibold mb-3 text-white text-sm">Follow Us</p>
              <div className="flex gap-3">
                {socialLinks.facebook?.enabled && socialLinks.facebook.url && (
                  <a 
                    href={socialLinks.facebook.url}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-primary hover:text-white transition-all duration-300"
                    aria-label="Facebook"
                  >
                    <Facebook size={18} />
                  </a>
                )}
                {socialLinks.instagram?.enabled && socialLinks.instagram.url && (
                  <a 
                    href={socialLinks.instagram.url}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-primary hover:text-white transition-all duration-300"
                    aria-label="Instagram"
                  >
                    <Instagram size={18} />
                  </a>
                )}
                {socialLinks.twitter?.enabled && socialLinks.twitter.url && (
                  <a 
                    href={socialLinks.twitter.url}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-primary hover:text-white transition-all duration-300"
                    aria-label="Twitter"
                  >
                    <Twitter size={18} />
                  </a>
                )}
                {socialLinks.linkedin?.enabled && socialLinks.linkedin.url && (
                  <a 
                    href={socialLinks.linkedin.url}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-primary hover:text-white transition-all duration-300"
                    aria-label="LinkedIn"
                  >
                    <Linkedin size={18} />
                  </a>
                )}
                {socialLinks.tiktok?.enabled && socialLinks.tiktok.url && (
                  <a 
                    href={socialLinks.tiktok.url}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-primary hover:text-white transition-all duration-300"
                    aria-label="TikTok"
                  >
                    <TikTokIcon size={18} />
                  </a>
                )}
                {socialLinks.youtube?.enabled && socialLinks.youtube.url && (
                  <a 
                    href={socialLinks.youtube.url}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-primary hover:text-white transition-all duration-300"
                    aria-label="YouTube"
                  >
                    <Youtube size={18} />
                  </a>
                )}
              </div>
            </div>
            
            <div className="hidden md:block space-y-6">
              <div>
                <p className="font-semibold mb-4 text-white text-sm">Quick Links</p>
                <ul className="space-y-2 text-sm text-gray-400">
                  {quickLinks.map((link, idx) => {
                    const isExternal = link.url.startsWith('http');
                    return (
                      <li key={idx}>
                        {isExternal ? (
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="hover:text-white transition-colors inline-flex items-center gap-1"
                          >
                            {link.label}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <NavLink to={link.url} className="hover:text-white transition-colors">
                            {link.label}
                          </NavLink>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
              
              {additionalServices.length > 0 && (
                <div>
                  <p className="font-semibold mb-4 text-white text-sm">Additional Services</p>
                  <ul className="space-y-2 text-sm text-gray-400">
                    {additionalServices
                      .filter((service) => !['Custom Supplements', 'Pet Tests'].includes(service.label))
                      .map((service, idx) => (
                      <li key={idx}>
                        <a 
                          href={service.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:text-white transition-colors inline-flex items-center gap-1"
                        >
                          {service.label}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </li>
                    ))}
                    
                    {/* Admin-only links */}
                    {isAdmin && (
                      <>
                        <li>
                          <NavLink 
                            to="/custom-supplements"
                            className="hover:text-white transition-colors"
                          >
                            Custom Supplements Page <span className="text-primary/70 text-xs">(Admin)</span>
                          </NavLink>
                        </li>
                        <li>
                          <NavLink 
                            to="/bodypanel"
                            className="hover:text-white transition-colors"
                          >
                            Bodypanel Page <span className="text-primary/70 text-xs">(Admin)</span>
                          </NavLink>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );

      case "quick_links":
        return (
          <div key="quick_links">
            <p className="font-semibold mb-4 text-white">Product Categories</p>
            <ul className="space-y-3">
              {(() => {
                // Filter and sort categories based on footer configuration
                const configMap = new Map(footerCategoriesConfig.map((c: any) => [c.id, c]));
                const products = productsResponse?.products || [];
                
                const enabledCategories = categories
                  .filter((cat: any) => {
                    const config = configMap.get(cat.id);
                    const isEnabled = config ? config.enabled : true;
                    if (!isEnabled) return false;
                    
                    // Only show categories that have products (flexible matching)
                    const hasProducts = products.some((p: any) => {
                      const productCategory = p.category?.toLowerCase().trim() || '';
                      const catName = cat.name?.toLowerCase().trim() || '';
                      const catSlug = cat.slug?.toLowerCase().trim() || '';
                      
                      // Match by name, slug, or converted slug
                      return productCategory === catName || 
                             productCategory === catSlug ||
                             productCategory.replace(/\s+/g, '-') === catSlug;
                    });
                    return hasProducts;
                  })
                  .map((cat: any) => ({
                    ...cat,
                    display_order: configMap.get(cat.id)?.display_order ?? 999
                  }))
                  .sort((a: any, b: any) => a.display_order - b.display_order);

                return enabledCategories.length > 0 ? enabledCategories.map((category: any) => (
                  <li key={category.id}>
                    <NavLink 
                      to={`/products?categories=${encodeURIComponent(category.name)}`}
                      className="flex items-center gap-3 group hover:text-white transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-gray-700 group-hover:ring-primary/50 transition-all flex-shrink-0">
                        {category.image_url ? (
                          <img 
                            src={category.image_url} 
                            alt={category.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20" />
                        )}
                      </div>
                      <span className="text-sm text-gray-400 group-hover:text-white transition-colors">
                        {category.name}
                      </span>
                    </NavLink>
                  </li>
                )) : (
                  <li className="text-sm text-gray-400">No categories selected</li>
                );
              })()}
            </ul>
            <div className="mt-4 pt-4 border-t border-gray-700 space-y-2">
              <NavLink 
                to="/brands"
                className="text-sm text-primary hover:text-primary/80 transition-colors font-medium flex items-center gap-2"
              >
                Browse All Brands
                <ExternalLink className="w-4 h-4" />
              </NavLink>
              <NavLink 
                to="/products"
                className="text-sm text-primary hover:text-primary/80 transition-colors font-medium flex items-center gap-2"
              >
                View All Supplements
                <ExternalLink className="w-4 h-4" />
              </NavLink>
            </div>
          </div>
        );

      case "top_products":
        return (
          <div key="top_products">
            <p className="font-semibold mb-4 text-white">Top Selling Products</p>
            <div className="grid grid-cols-1 gap-3">
              {topProducts.map((product: any) => (
                <NavLink 
                  key={product.id}
                  to={`/product/${product.slug}`}
                  className="group flex items-center gap-3 p-2 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-gray-700 group-hover:ring-primary/50 transition-all flex-shrink-0">
                    {product.image_url ? (
                      <img 
                        src={product.image_url} 
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="text-xs text-gray-400 group-hover:text-white transition-colors line-clamp-2">
                      {product.title}
                    </h5>
                    {product.price && (
                      <p className="text-xs text-primary font-semibold mt-1">
                        ${Number(product.price).toFixed(2)}
                      </p>
                    )}
                  </div>
                </NavLink>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-700">
              <NavLink 
                to="/products?sort=popularity"
                className="text-sm text-primary hover:text-primary/80 transition-colors font-medium flex items-center gap-2"
              >
                View All Popular Products
                <ExternalLink className="w-4 h-4" />
              </NavLink>
            </div>
          </div>
        );

      case "health_image":
        return (
          <div key="health_image" className="hidden lg:block self-start">
            <p className="font-semibold mb-4 text-white">Health & Wellness</p>
            <div className="relative h-64 rounded-lg overflow-hidden">
              <img 
                src={footerHealthImage} 
                alt="Health and Wellness" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 to-transparent" />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Show loading state while CMS data is loading
  if (settingsLoading || categoriesLoading) {
    return (
      <footer className={`mt-auto border-t bg-gray-900 text-gray-100 ${isStickyCartVisible ? 'pb-24 md:pb-0' : ''}`}>
        <div className="container mx-auto px-6 md:px-8 py-12">
          <div className="text-center text-gray-400">Loading...</div>
        </div>
      </footer>
    );
  }

  return (
    <footer className={`mt-auto border-t bg-gray-900 text-gray-100 ${isStickyCartVisible ? 'pb-24 md:pb-0' : ''}`}>
      <div className="container mx-auto px-6 md:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-8">
          {sectionsOrder.filter((s: any) => s.enabled).map((section: any) => renderSection(section.id))}

          {/* Quick Links on Mobile - After Products */}
          <div className="md:hidden space-y-6">
            <div>
              <p className="font-semibold mb-4 text-white text-sm">Quick Links</p>
              <ul className="space-y-2 text-sm text-gray-400">
                {quickLinks.map((link, idx) => {
                  const isExternal = link.url.startsWith('http');
                  return (
                    <li key={idx}>
                      {isExternal ? (
                        <a 
                          href={link.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:text-white transition-colors inline-flex items-center gap-1"
                        >
                          {link.label}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <NavLink to={link.url} className="hover:text-white transition-colors">
                          {link.label}
                        </NavLink>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            
            {additionalServices.length > 0 && (
              <div>
                <p className="font-semibold mb-4 text-white text-sm">Additional Services</p>
                <ul className="space-y-2 text-sm text-gray-400">
                  {additionalServices.map((service, idx) => (
                    <li key={idx}>
                      <a 
                        href={service.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-white transition-colors inline-flex items-center gap-1"
                      >
                        {service.label}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        
        <div className="pt-8 border-t border-gray-700">
          <div className="mb-8">
            <div className="max-w-md mx-auto text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Mail className="w-5 h-5 text-primary" />
                <p className="font-semibold text-white">Subscribe to Our Newsletter</p>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Get the latest health tips, product updates, and exclusive offers.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-primary"
                  disabled={isSubmitting}
                  maxLength={255}
                />
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="whitespace-nowrap"
                >
                  {isSubmitting ? "Subscribing..." : "Subscribe"}
                </Button>
              </form>
            </div>
          </div>

          {/* Trust Badges & Payment Methods - Hidden on Mobile */}
          {(trustBadges.length > 0 || paymentMethods.length > 0) && (
            <div className="hidden md:block mb-8 pb-8 border-b border-gray-700">
              <div className="max-w-4xl mx-auto">
                {trustBadges.length > 0 && (
                  <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 mb-6">
                    {trustBadges.map((badge: any) => {
                      const getIconColor = (color: string) => {
                        const colors: Record<string, string> = {
                          green: 'text-green-500 bg-green-500/10',
                          blue: 'text-blue-500 bg-blue-500/10',
                          primary: 'text-primary bg-primary/10',
                          purple: 'text-purple-500 bg-purple-500/10',
                        };
                        return colors[color] || 'text-gray-500 bg-gray-500/10';
                      };

                      const getIcon = (id: string) => {
                        if (id === 'ssl') return (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        );
                        if (id === 'payments') return (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                        );
                        if (id === 'guarantee') return (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        );
                        return (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                          </svg>
                        );
                      };

                      return (
                        <div key={badge.id} className="flex items-center gap-2 text-gray-400">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getIconColor(badge.color)}`}>
                            {getIcon(badge.id)}
                          </div>
                          <div className="text-left">
                            <div className="text-xs font-semibold text-white">{badge.title}</div>
                            <div className="text-xs">{badge.subtitle}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {paymentMethods.length > 0 && (
                  <div className="text-center">
                    <p className="text-xs text-gray-300 mb-3">We Accept</p>
                    <div className="flex items-center justify-center gap-4 flex-wrap">
                      {paymentMethods.map((method: any) => {
                        const getMethodDisplay = (id: string) => {
                          if (id === 'visa') return <span className="text-xs font-bold text-blue-700">VISA</span>;
                          if (id === 'mastercard') return (
                            <span className="text-xs font-bold">
                              <span className="text-red-600">●</span>
                              <span className="text-orange-500">●</span>
                            </span>
                          );
                          if (id === 'amex') return <span className="text-xs font-bold text-blue-800">AMEX</span>;
                          if (id === 'paypal') return <span className="text-xs font-bold text-blue-900">PayPal</span>;
                          if (id === 'googlepay') return <span className="text-xs font-bold text-green-700">GPay</span>;
                          if (id === 'applepay') return <span className="text-xs font-bold text-gray-800">Pay</span>;
                          return <span className="text-xs font-bold text-gray-800">{method.name}</span>;
                        };

                        return (
                          <div key={method.id} className="w-12 h-8 bg-white rounded flex items-center justify-center">
                            {getMethodDisplay(method.id)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <p className="text-sm text-gray-400 text-center">
            © 2025 GetTested. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
